import { computeOrderTaxes } from "./tax/itemTax";
import { isSequentialChannelNumber, isWorkingSessionId } from "./pos/sessionDisplayName";
import { isTableChannelNumber } from "./orderNumberGenerator";

const CHANNEL_NAME = "nuage-customer-screen";
const TICKET_KEY = "nuageCustomerTicket";
const PAYMENT_KEY = "nuageCustomerPayment";
const CAPTURE_KEY = "nuageCustomerCapture";
const DISPLAY_KEY = "nuageCustomerDisplay";
export const CUSTOMER_PIN_KEY = "nuageCustomerWindowPin";

const handlers = new Set();
let wired = false;
let bus = null;

const parse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const post = (message) => {
  try {
    ensure();
    bus?.postMessage(message);
  } catch {
    /* BroadcastChannel is unavailable in this window. */
  }
  handlers.forEach((handler) => handler(message));
};

const ensure = () => {
  if (wired || typeof window === "undefined") return;
  wired = true;
  try {
    bus = new BroadcastChannel(CHANNEL_NAME);
    bus.onmessage = (event) => {
      handlers.forEach((handler) => handler(event.data));
    };
  } catch {
    bus = null;
  }
  window.addEventListener("storage", (event) => {
    if (event.key === TICKET_KEY) handlers.forEach((handler) => handler({ type: "ticket", ticket: parse(event.newValue) }));
    if (event.key === PAYMENT_KEY) handlers.forEach((handler) => handler({ type: "payment", payment: parse(event.newValue) }));
    if (event.key === CAPTURE_KEY) handlers.forEach((handler) => handler({ type: "capture", capture: parse(event.newValue) }));
    if (event.key === DISPLAY_KEY) handlers.forEach((handler) => handler({ type: "display", display: parse(event.newValue) }));
  });
};

export const subscribeCustomerScreen = (handler) => {
  ensure();
  handlers.add(handler);
  return () => handlers.delete(handler);
};

export const readTicket = () => parse(localStorage.getItem(TICKET_KEY));
export const readPayment = () => parse(localStorage.getItem(PAYMENT_KEY));
export const readCapture = () => parse(localStorage.getItem(CAPTURE_KEY));
export const readDisplay = () => parse(localStorage.getItem(DISPLAY_KEY));
export const readCustomerPin = () => parse(localStorage.getItem(CUSTOMER_PIN_KEY));

export const publishTicket = (ticket) => {
  localStorage.setItem(TICKET_KEY, JSON.stringify(ticket));
  post({ type: "ticket", ticket });
};

export const publishPayment = (payment) => {
  if (!payment) localStorage.removeItem(PAYMENT_KEY);
  else localStorage.setItem(PAYMENT_KEY, JSON.stringify(payment));
  post({ type: "payment", payment: payment || null });
};

export const writeCapture = (capture) => {
  if (!capture || capture.open === false) {
    localStorage.removeItem(CAPTURE_KEY);
    post({ type: "capture", capture: null });
    return;
  }
  localStorage.setItem(CAPTURE_KEY, JSON.stringify(capture));
  post({ type: "capture", capture });
};

export const clearCapture = () => writeCapture(null);

export const publishDisplay = (display) => {
  localStorage.setItem(DISPLAY_KEY, JSON.stringify(display));
  post({ type: "display", display });
};

export const publishChoose = (customer) => post({ type: "choose", customer });
export const publishConfirm = () => post({ type: "confirm", capture: readCapture() });

export const captureSteps = (capture) => {
  if (capture?.kind === "receipt") return capture.skipName ? ["email"] : ["email", "name"];
  if (capture?.kind === "phone") return ["phone", "name", "email"];
  return ["email", "phone", "name"];
};

export const initialCapture = (session, kind) => {
  const phoneOrder = kind === "phone" || session?.isPhoneOrder === true;
  const receipt = kind === "receipt";
  return {
    open: true,
    kind: receipt ? "receipt" : phoneOrder ? "phone" : "add",
    step: receipt ? "email" : phoneOrder ? "phone" : "email",
    email: "",
    phone: "",
    firstName: "",
    lastName: "",
    nameTarget: "firstName",
    emailRequired: receipt,
    phoneRequired: phoneOrder,
  };
};

const optionDetail = (item) => {
  const selections = item?.advancedOptions?.selections || [];
  if (!Array.isArray(selections)) return "";
  return selections.map((selection) => selection?.name || selection?.optionName || "").filter(Boolean).join(", ");
};

export const ticketFromCart = (items, tableName) => ({
  tableName: tableName || "",
  items: (items || []).map((item, index) => ({
    key: `${item.id || "item"}-${item.seatNumber || 0}-${index}`,
    name: item.name || item.itemName || "",
    amount: Number(item.amount) || 0,
    price: Number(item.price) || 0,
    image: typeof item.image === "string" ? item.image : "",
    detail: optionDetail(item),
    taxIds: Array.isArray(item.taxIds) ? item.taxIds : undefined,
  })),
});

const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

export const ticketMoney = (items, store) => {
  const lines = Array.isArray(items) ? items : [];
  const subtotal = roundMoney(lines.reduce((sum, item) => sum + (Number(item.price) || 0) * (Number(item.amount) || 0), 0));
  const taxes = store
    ? computeOrderTaxes(lines, store)
      .filter((tax) => String(tax.taxName || "").trim())
      .map((tax) => ({ name: tax.taxName, amount: roundMoney(tax.taxAmount) }))
    : [];
  const taxTotal = roundMoney(taxes.reduce((sum, tax) => sum + tax.amount, 0));
  return { subtotal, taxes, taxTotal, total: roundMoney(subtotal + taxTotal) };
};

const channelFromId = (value) => {
  const text = String(value || "");
  if (/^(CS|virtual_CS)/i.test(text)) return "COUNTER";
  if (/^(TW|virtual_TW)/i.test(text)) return "TAKEAWAY";
  if (/^(DL|virtual_DL)/i.test(text)) return "DELIVERY";
  if (text) return "TABLE";
  return "";
};

const naturalChannelNumber = (value) => {
  const text = String(value || "");
  const exact = text.trim().match(/^(CS|TW|DL)([B-D])?(Z|F)?(\d+)$/i);
  const embedded = exact || (isWorkingSessionId(text) ? null : text.match(/(CS|TW|DL)([B-D])?(Z|F)?(\d+)/i));
  if (!embedded) return "";
  const width = Math.max(3, embedded[4].length);
  return `${embedded[1].toUpperCase()}${(embedded[2] || "").toUpperCase()}${(embedded[3] || "").toUpperCase()}${String(Number(embedded[4])).padStart(width, "0")}`;
};

const readableTableName = (value) => {
  const text = String(value || "").trim();
  if (!text || isWorkingSessionId(text) || isSequentialChannelNumber(text)) return "";
  if (/^virtual_/i.test(text)) return "";
  if (/^[A-Za-z0-9]{16,}$/.test(text)) return "";
  return text;
};

export const customerTicketPlace = ({ sessionId, orderNumber, orderChannel, tableName } = {}) => {
  const channel = orderChannel || channelFromId(sessionId) || channelFromId(tableName) || "COUNTER";
  const natural = naturalChannelNumber(orderNumber) || naturalChannelNumber(tableName) || naturalChannelNumber(sessionId);
  if (channel === "TABLE") {
    const name = readableTableName(tableName) || readableTableName(sessionId);
    const party = isTableChannelNumber(sessionId) ? String(sessionId).trim() : "";
    const namedParty = isTableChannelNumber(tableName) ? String(tableName).trim() : "";
    return { channel, labelKey: "table", value: (name && !isTableChannelNumber(name) ? name : "") || party || namedParty || "" };
  }
  const labelKey = channel === "TAKEAWAY"
    ? "takeaway"
    : (channel === "DELIVERY" || channel === "DELIVERY_LOCAL")
      ? "delivery"
      : channel === "ONLINE"
        ? "online"
        : "counter";
  return { channel, labelKey, value: natural };
};
